import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { trackPageView } from "../lib/analytics";
import { COOKIE_CONSENT_EVENT, type CookieConsent } from "../lib/cookie-consent";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { absoluteUrl, ORGANIZATION_SCHEMA, SOCIAL_IMAGE_PATH, WEBSITE_SCHEMA } from "../lib/seo";
import { CartProvider } from "@/features/sales/storefront/cart/CartContext";
import { AuthProvider } from "@/features/auth/AuthContext";
import { CatalogProvider } from "@/features/sales/storefront/catalog/CatalogContext";
import { CurrencyProvider } from "@/features/sales/storefront/currency/CurrencyContext";
import { Toaster } from "@/components/ui/sonner";
import { CookiePreferences } from "@/features/legal/CookiePreferences";

function NotFoundComponent() {
  return (
    <main
      id="main-content"
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscas no existe o ha sido movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </main>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main
      id="main-content"
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página no cargó
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo salió mal. Puedes reintentar o volver al inicio.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Reintentar
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Socrattica" },
      {
        name: "description",
        content: "Librería digital y podcast para aprender inglés a tu ritmo.",
      },
      { name: "msvalidate.01", content: "6A3564FDD6AE4518EB83EE50B688EA2D" },
      { property: "og:site_name", content: "Socrattica" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: absoluteUrl(SOCIAL_IMAGE_PATH) },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "Socrattica: menos vueltas, más progreso al aprender inglés",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(SOCIAL_IMAGE_PATH) },
      { name: "theme-color", content: "#000000" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "icon",
        type: "image/png",
        href: "/favicon-32x32.png",
        sizes: "32x32",
      },
      {
        rel: "icon",
        type: "image/png",
        href: "/favicon-16x16.png",
        sizes: "16x16",
      },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      {
        rel: "preload",
        href: "/fonts/poppins-600-latin.woff2",
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="es">
      <head>
        <HeadContent />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ORGANIZATION_SCHEMA) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(WEBSITE_SCHEMA) }}
        />
      </head>
      <body>
        <a
          href="#main-content"
          className="fixed left-4 top-4 z-[100] -translate-y-24 rounded-md bg-black px-4 py-2 text-sm font-semibold text-white transition-transform focus:translate-y-0"
        >
          Saltar al contenido
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();

  useEffect(() => {
    void trackPageView(`${location.pathname}${location.searchStr}`, document.title);

    const trackAfterConsent = (event: Event) => {
      const consent = (event as CustomEvent<CookieConsent>).detail;
      if (consent?.analytics) {
        void trackPageView(`${location.pathname}${location.searchStr}`, document.title);
      }
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, trackAfterConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, trackAfterConsent);
  }, [location.pathname, location.searchStr]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CurrencyProvider>
          <CatalogProvider>
            <CartProvider>
              <Outlet />
              <Toaster />
              <CookiePreferences />
            </CartProvider>
          </CatalogProvider>
        </CurrencyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
